import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Square } from 'lucide-react'
import { clsx } from 'clsx'
import { useProviderAuth } from '@hooks/useProviderAuth'
import type { ProviderInductionChapterKey } from '@lib/supabase/providerAuth.service'

type TrainingChapter = {
  key: ProviderInductionChapterKey
  title: string
  subtitle: string
  steps: string[]
  acknowledgement: string
}

const TRAINING_CHAPTERS: TrainingChapter[] = [
  {
    key: 'ndpr',
    title: 'NDPR: Data Privacy For Patient Records',
    subtitle: 'Protect patient confidentiality and process data lawfully.',
    steps: [
      'Collect only information required to deliver care safely and effectively.',
      'Never share patient records outside approved HomiCareplus channels.',
      'Use secure devices, strong authentication, and report suspected data breaches immediately.',
    ],
    acknowledgement: 'I understand and will apply these data privacy practices.',
  },
  {
    key: 'safety_security',
    title: 'Safety & Security: Exit-First Protocols',
    subtitle: 'Prioritize personal safety in unfamiliar environments.',
    steps: [
      'Before each visit, review address details and confirm identity signals in-app.',
      'On arrival, identify the fastest safe exit route and keep phone access ready.',
      'If a setting feels unsafe, de-escalate, exit first, and document the visit immediately.',
    ],
    acknowledgement: 'I understand and will apply these safety protocols.',
  },
  {
    key: 'platform_rules',
    title: 'Platform Rules: Non-Circumvention',
    subtitle: 'All bookings and payments must remain on-platform.',
    steps: [
      'Do not request, accept, or suggest off-platform payments from patients or families.',
      'Do not share personal payment details for appointments initiated on HomiCareplus.',
      'Report patient attempts to move payments off-platform through support immediately.',
    ],
    acknowledgement: 'I understand and will comply with these platform rules.',
  },
  {
    key: 'code_conduct',
    title: 'Provider Code Of Conduct & Emergency SOP',
    subtitle: 'Maintain professionalism, ethics, and escalation discipline.',
    steps: [
      'Treat patients and households with dignity, respect, and clear communication.',
      'Follow clinical boundaries and documentation standards, and avoid harassment or misconduct.',
      'For emergencies, stabilize safely, contact emergency services, then escalate through app support.',
    ],
    acknowledgement: 'I understand and will apply these guidelines.',
  },
  {
    key: 'terms_service',
    title: 'Terms Of Service',
    subtitle: 'Understand legal obligations and platform enforcement.',
    steps: [
      'Account integrity, truthful profile details, and valid credentials are mandatory.',
      'Violations of payment, safety, or misconduct policies may trigger suspension or deactivation.',
      'You are responsible for complying with all applicable healthcare and privacy regulations.',
    ],
    acknowledgement: 'I understand and will comply with these terms.',
  },
  {
    key: 'app_usage',
    title: 'How To Use The App',
    subtitle: 'Operate core provider workflows correctly inside HomiCareplus.',
    steps: [
      'Use Appointments to accept or reject requests, update status, and close visits accurately.',
      'Use Chat for patient communication and keep all care-related messages in-app.',
      'Use Profile and Availability to keep service areas, pricing, and schedule up to date.',
    ],
    acknowledgement: 'I understand and will apply these app usage practices.',
  },
]

const EMPTY_ACKS: Record<ProviderInductionChapterKey, boolean> = {
  ndpr: false,
  safety_security: false,
  platform_rules: false,
  code_conduct: false,
  terms_service: false,
  app_usage: false,
}

export default function ProviderInduction() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isRevisitMode = searchParams.get('mode') === 'revisit'
  const {
    provider,
    getInductionStatus,
    getInductionProgress,
    saveInductionProgress,
    acknowledgeInductionChapter,
    completeInduction,
  } = useProviderAuth()

  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isMandatory, setIsMandatory] = React.useState(!isRevisitMode)
  const [chapterIndex, setChapterIndex] = React.useState(0)
  const [stepIndex, setStepIndex] = React.useState(0)
  const [error, setError] = React.useState<string | null>(null)
  const [chapterAcknowledgements, setChapterAcknowledgements] = React.useState<Record<ProviderInductionChapterKey, boolean>>(EMPTY_ACKS)

  const currentChapter = TRAINING_CHAPTERS[chapterIndex]
  const currentStep = currentChapter.steps[stepIndex]
  const isEndOfChapter = stepIndex === currentChapter.steps.length - 1
  const isLastChapter = chapterIndex === TRAINING_CHAPTERS.length - 1
  const isCurrentChapterAcknowledged = chapterAcknowledgements[currentChapter.key]

  const totalSteps = React.useMemo(
    () => TRAINING_CHAPTERS.reduce((sum, chapter) => sum + chapter.steps.length, 0),
    [],
  )

  const completedSteps = React.useMemo(() => {
    const stepsBeforeCurrent = TRAINING_CHAPTERS.slice(0, chapterIndex).reduce(
      (sum, chapter) => sum + chapter.steps.length,
      0,
    )
    return stepsBeforeCurrent + stepIndex + 1
  }, [chapterIndex, stepIndex])

  const closeInduction = React.useCallback(() => {
    navigate(isRevisitMode ? '/provider/profile' : '/provider/dashboard', { replace: true })
  }, [isRevisitMode, navigate])

  React.useEffect(() => {
    let isMounted = true

    const loadProgress = async () => {
      if (!provider?.id) {
        if (isMounted) {
          setIsLoading(false)
        }
        return
      }

      const [status, progressResult] = await Promise.all([
        getInductionStatus(),
        getInductionProgress(),
      ])

      if (!isMounted) {
        return
      }

      const progress = progressResult.progress || status.progress
      if (progress) {
        const normalizedChapterIndex = Math.max(0, Math.min(TRAINING_CHAPTERS.length - 1, (progress.current_chapter || 1) - 1))
        const chapterStepCount = TRAINING_CHAPTERS[normalizedChapterIndex].steps.length
        const normalizedStepIndex = Math.max(0, Math.min(chapterStepCount - 1, (progress.current_step || 1) - 1))

        setChapterIndex(normalizedChapterIndex)
        setStepIndex(normalizedStepIndex)
        setChapterAcknowledgements({
          ndpr: !!progress.ndpr_acknowledged,
          safety_security: !!progress.safety_security_acknowledged,
          platform_rules: !!progress.platform_rules_acknowledged,
          code_conduct: !!progress.code_conduct_acknowledged,
          terms_service: !!progress.terms_service_acknowledged,
          app_usage: !!progress.app_usage_acknowledged,
        })
      }

      setIsMandatory(status.needsInduction)

      if (!status.needsInduction && !isRevisitMode) {
        navigate('/provider/dashboard', { replace: true })
        return
      }

      setIsLoading(false)
    }

    void loadProgress()

    return () => {
      isMounted = false
    }
  }, [provider?.id, getInductionStatus, getInductionProgress, isRevisitMode, navigate])

  const persistCursor = React.useCallback(async (nextChapterIndex: number, nextStepIndex: number) => {
    setError(null)
    const result = await saveInductionProgress(nextChapterIndex + 1, nextStepIndex + 1)
    if (!result.success) {
      setError(result.error || 'Unable to save training progress. Please try again.')
      return false
    }

    setChapterIndex(nextChapterIndex)
    setStepIndex(nextStepIndex)
    return true
  }, [saveInductionProgress])

  const handlePrevious = async () => {
    if (isSaving) return

    if (stepIndex > 0) {
      setIsSaving(true)
      await persistCursor(chapterIndex, stepIndex - 1)
      setIsSaving(false)
      return
    }

    if (chapterIndex > 0) {
      const prevChapterIndex = chapterIndex - 1
      const prevStepIndex = TRAINING_CHAPTERS[prevChapterIndex].steps.length - 1
      setIsSaving(true)
      await persistCursor(prevChapterIndex, prevStepIndex)
      setIsSaving(false)
    }
  }

  const handleNext = async () => {
    if (isSaving) return

    if (isEndOfChapter && !isCurrentChapterAcknowledged) {
      setError('Please acknowledge this chapter before continuing.')
      return
    }

    setIsSaving(true)
    setError(null)

    if (isEndOfChapter) {
      const ackResult = await acknowledgeInductionChapter(currentChapter.key, chapterIndex + 1, stepIndex + 1)
      if (!ackResult.success) {
        setIsSaving(false)
        setError(ackResult.error || 'Could not save chapter acknowledgement.')
        return
      }

      setChapterAcknowledgements((prev) => ({
        ...prev,
        [currentChapter.key]: true,
      }))

      if (isLastChapter) {
        const completed = await completeInduction()
        setIsSaving(false)

        if (!completed.success) {
          setError(completed.error || 'Unable to finalize training. Please retry.')
          return
        }

        navigate('/provider/dashboard', { replace: true })
        return
      }

      await persistCursor(chapterIndex + 1, 0)
      setIsSaving(false)
      return
    }

    await persistCursor(chapterIndex, stepIndex + 1)
    setIsSaving(false)
  }

  if (isLoading) {
    return (
      <div className="min-h-full bg-muted/40 flex items-center justify-center px-4 py-10">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          Loading HomiCareplus Provider Training...
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.10),_transparent_35%),linear-gradient(180deg,_rgba(248,250,252,0.98),_rgba(255,255,255,1))]">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <div className="border border-border bg-card shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          <div className="border-b border-border p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <div>
                  <h1 className="text-2xl font-bold text-foreground sm:text-3xl">HomiCareplus Provider Training</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Complete all six chapters before you continue using your provider account.
                  </p>
                </div>
              </div>

              {!isMandatory && (
                <button
                  type="button"
                  title="Back to provider profile"
                  onClick={closeInduction}
                  className="inline-flex items-center gap-2 border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                >
                  <ArrowLeft size={16} />
                  Back
                </button>
              )}
            </div>

            <div className="mt-5">
              <div className="grid grid-cols-[repeat(18,minmax(0,1fr))] gap-1">
                {Array.from({ length: totalSteps }).map((_, index) => (
                  <div
                    key={index}
                    className={clsx(
                      'h-2 rounded-full transition-colors duration-300',
                      index < completedSteps ? 'bg-primary' : 'bg-muted',
                    )}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Chapter {chapterIndex + 1} of {TRAINING_CHAPTERS.length} • Step {stepIndex + 1} of {currentChapter.steps.length}
              </p>
            </div>
          </div>

          <div className="grid gap-0 lg:grid-cols-[260px_minmax(0,1fr)]">
            <aside className="border-b border-border bg-muted/20 p-5 lg:border-b-0 lg:border-r sm:p-6">
              <div className="space-y-3">
                {TRAINING_CHAPTERS.map((chapter, index) => {
                  const isActive = index === chapterIndex
                  const isDone = chapterAcknowledgements[chapter.key]

                  return (
                    <div
                      key={chapter.key}
                      className={clsx(
                        'border p-3 transition-colors',
                        isActive ? 'border-primary bg-primary/5' : 'border-border bg-card',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Chapter {index + 1}</p>
                          <p className="mt-1 text-sm font-semibold text-foreground">{chapter.title}</p>
                        </div>
                        {isDone ? <Check size={16} className="shrink-0 text-emerald-600" /> : null}
                      </div>
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">{chapter.subtitle}</p>
                    </div>
                  )
                })}
              </div>
            </aside>

            <section className="p-5 sm:p-6">
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Current Chapter</p>
                  <h2 className="mt-2 text-xl font-bold text-foreground">{currentChapter.title}</h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{currentChapter.subtitle}</p>
                </div>

                <div className="space-y-3 border border-border p-4 sm:p-5">
                  <p className="text-sm font-semibold text-foreground">Chapter checklist</p>
                  {currentChapter.steps.map((step, index) => {
                    const isCurrent = index === stepIndex
                    const isPast = index < stepIndex
                    return (
                      <div key={step} className="flex items-start gap-3">
                        <div className={clsx(
                          'mt-0.5 flex h-5 w-5 items-center justify-center border text-[11px] font-semibold',
                          isCurrent ? 'border-primary bg-primary text-primary-foreground' : isPast ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border bg-card text-muted-foreground',
                        )}>
                          {isPast ? <Check size={12} /> : index + 1}
                        </div>
                        <p className={clsx('text-sm leading-6', isCurrent ? 'text-foreground font-medium' : 'text-muted-foreground')}>
                          {step}
                        </p>
                      </div>
                    )
                  })}
                </div>

                <div className="border border-primary bg-primary/15 p-4 sm:p-5">
                  <div>
                    <p className="mt-2 text-base font-semibold leading-7 text-foreground sm:text-lg">{currentStep}</p>
                  </div>
                </div>

                {isEndOfChapter ? (
                  <button
                    type="button"
                    onClick={() => {
                      setError(null)
                      setChapterAcknowledgements((prev) => ({
                        ...prev,
                        [currentChapter.key]: !prev[currentChapter.key],
                      }))
                    }}
                    className="flex w-full items-start gap-3 border border-border bg-muted/20 p-4 text-left hover:bg-muted/40"
                  >
                    {isCurrentChapterAcknowledged ? (
                      <Check size={18} className="mt-0.5 shrink-0 text-primary" />
                    ) : (
                      <Square size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="text-sm leading-6 text-foreground">{currentChapter.acknowledgement}</span>
                  </button>
                ) : null}

                {error ? (
                  <div className="border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {error}
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    onClick={() => void handlePrevious()}
                    disabled={isSaving || (chapterIndex === 0 && stepIndex === 0)}
                    className="inline-flex items-center justify-center gap-2 border border-border px-4 py-3 text-sm font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleNext()}
                    disabled={isSaving || (isEndOfChapter && !isCurrentChapterAcknowledged)}
                    className="inline-flex items-center justify-center gap-2 bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSaving ? 'Saving...' : isLastChapter && isEndOfChapter ? 'Finish Training' : 'Continue'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}